import { Button, Divider, Stack, TextField, Typography } from '@mui/material';
import { createFileRoute, redirect, useRouter } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import logo from '../assets/images/logo.svg';
import BackButton from '../components/BackButton';
import Loader from '../components/Loader';
import { useAuthenticationContext } from '../contexts/AuthenticationContext';
import { useUserAppContext } from '../contexts/UserAppContext';
import MicrosoftLogo from '../features/bed-management/components/MicrosoftLogo';

export const Route = createFileRoute('/login')({
  component: RouteComponent,
  beforeLoad: ({ context }) => {
    if (!context.userAppContext.app) {
      throw redirect({ to: '/app-select' });
    }
    if (context.healthCenterContext.healthCenterConfig?.id) {
      throw redirect({ to: '/porter-login' });
    }
    if (context.authenticationContext.isAuthenticated) {
      throw redirect({ to: '/' });
    }
  },
});

const LOGO_HEIGHT = 50;

function RouteComponent() {
  const { t } = useTranslation('common');
  const router = useRouter();
  const { login, microsoftLogin, loading, loginError, microsoftError } = useAuthenticationContext();
  const { resetApp } = useUserAppContext();
  const navigate = Route.useNavigate();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await login(username, password);
    await router.invalidate();
  };

  const handleMicrosoftLogin = async () => {
    await microsoftLogin();
    await router.invalidate();
  };

  if (loading) {
    return <Loader />;
  }

  const handleBack = () => {
    resetApp();
    navigate({ to: '/app-select' });
  };

  return (
    <Stack spacing={12} paddingX={12} justifyContent="center" height="100%">
      <BackButton onClick={handleBack} />
      <Stack spacing={4} alignItems="center">
        <img src={logo} alt="logo" height={LOGO_HEIGHT} />
      </Stack>
      <Stack alignItems="center">
        <Stack spacing={5} maxWidth={600} width="100%">
          <Stack spacing={5} component="form" onSubmit={handleSubmit}>
            <Stack spacing={3}>
              <TextField
                label={t('auth.username')}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                fullWidth
                autoFocus
                inputProps={{
                  autoCapitalize: 'none',
                }}
              />

              <TextField
                label={t('auth.password')}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                fullWidth
              />

              {loginError && (
                <Typography color="error" variant="body2">
                  {loginError}
                </Typography>
              )}
            </Stack>
            <Button type="submit" fullWidth disabled={!username || !password}>
              {t('auth.loginButton')}
            </Button>
          </Stack>

          <Divider sx={{ marginTop: 3, marginBottom: 2 }}>
            <Typography color="secondary">{t('auth.or')}</Typography>
          </Divider>

          {microsoftError && (
            <Typography color="error" variant="body2" textAlign="center">
              {microsoftError}
            </Typography>
          )}

          <Button
            variant="outlined"
            fullWidth
            onClick={handleMicrosoftLogin}
            startIcon={<MicrosoftLogo height={16} width={16} />}
          >
            {t('auth.microsoftLogin', { ns: 'bed-management' })}
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
}
